import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ProfileService } from '../../../common/service/data/profile.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { isRealChange } from '../../../core/helper/simple-changes-typed';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString } from '../../../invoice/model/const/enum';
import { CompanyService } from '../../../rent/service/data/company.service';

@Component({
  selector: 'app-invoice-delivery-method',
  templateUrl: './delivery-method.component.html',
  styleUrls: ['./delivery-method.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DeliveryMethodComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private readonly _profileService: ProfileService,
    private readonly _companyService: CompanyService
  ) {
    this.defaultItems = [];
    this.items = this.defaultItems; // Important: reference equality to defaultItems!
  }

  private _getDefaultSub: Subscription;
  private _getMethodsSub: Subscription;

  public defaultMethod: InvoiceDeliveryMethod;
  public defaultItems: SelectionItem<InvoiceDeliveryMethod>[];
  public items: SelectionItem<InvoiceDeliveryMethod>[];

  //@Input({ required: true }) control: FormControl<InvoiceDeliveryMethod>; // TODO: Angular 16+
  // Accept AbstractControl for template binding; used internally as FormControl.
  private _control: FormControl<InvoiceDeliveryMethod>;
  @Input() set control(value: AbstractControl) {
    this._control = value as FormControl<InvoiceDeliveryMethod>;
  }
  get control(): FormControl<InvoiceDeliveryMethod> {
    return this._control;
  }
  @Input() company: string;
  @Input() payer: string;
  @Input() allowNoDelivery: boolean = false;
  @Input() allowedMethods: InvoiceDeliveryMethod[];
  @Input() helpText: string;

  ngOnInit(): void {
    if (!this.control) {
      console.error('Must bind DeliveryMethodComponent [control] attribute');
    }

    this._updateDefaultItems();
    if (!this.control.value) {
      this._applyDefaultInvoiceDeliveryMethod();
    }
    this._getInvoiceDeliveryMethods();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (isRealChange(changes['allowNoDelivery']) || isRealChange(changes['allowedMethods'])) {
      this._updateDefaultItems();
    }

    const isPayerChange = isRealChange(changes['payer']);
    if (isPayerChange || isRealChange(changes['company'])) {
      this._getInvoiceDeliveryMethods(isPayerChange);
    }
  }

  ngOnDestroy(): void {
    if (this._getDefaultSub) {
      this._getDefaultSub.unsubscribe();
      this._getDefaultSub = null;
    }
    if (this._getMethodsSub) {
      this._getMethodsSub.unsubscribe();
      this._getMethodsSub = null;
    }
  }

  public setDeliveryMethods(newMethods: InvoiceDeliveryMethod[]): void {
    this.items = this._filterAllowedMethods(newMethods).map(
      m => new SelectionItem(InvoiceDeliveryMethodToString(m), m, null, null, GetInvoiceDeliveryMethodIcon(m))
    );
    this._applyDefaultIfSelectedItemIsInvalid();
  }

  private _applyDefaultIfSelectedItemIsInvalid(): void {
    if (!this.items.some(x => x.value === this.control.value)) {
      this._applyDefaultInvoiceDeliveryMethod();
    }
  }

  private _updateDefaultItems(): void {
    let items: SelectionItem<InvoiceDeliveryMethod>[] = [
      {
        name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail),
        value: InvoiceDeliveryMethod.Mail,
        icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail),
      },
      {
        name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual),
        value: InvoiceDeliveryMethod.Manual,
        icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual),
      },
      {
        name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF),
        value: InvoiceDeliveryMethod.EmailWithPDF,
        icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF),
      },
      {
        name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF),
        value: InvoiceDeliveryMethod.EmailWithoutPDF,
        icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF),
      },
      {
        name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B),
        value: InvoiceDeliveryMethod.EInvoiceB2B,
        icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B),
      },
    ];
    if (this.allowNoDelivery) {
      items.push({ name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.NoDelivery), value: InvoiceDeliveryMethod.NoDelivery });
    }
    items = items.filter(item => this._isAllowedMethod(item.value));
    if (this.items === this.defaultItems) {
      // Where reference equality to defaultItems comes into play. (See ctor)
      this.items = items;
    }
    this.defaultItems = items;
  }

  private _applyDefaultInvoiceDeliveryMethod(): void {
    if (this.defaultMethod !== undefined) {
      this.control.setValue(this.defaultMethod); // has fetched already (seldom changes -- do not refetch)
    }
    if (this._getDefaultSub) {
      return; // fetch currently in progress
    }

    this.control.setValue(InvoiceDeliveryMethod.Mail); // this pre-fetch "reset" is required for "weak guard" below!
    this._getDefaultSub = this._profileService.getConfig().subscribe(profile => {
      // weak guard against race-condition vs getInvoiceDeliveryMethods(isPayerChange: true)
      if (this.control.value !== InvoiceDeliveryMethod.EInvoiceB2C) {
        this.control.setValue(profile.invoiceDeliveryMethod);
      }
      this.defaultMethod = profile.invoiceDeliveryMethod;
      this._getDefaultSub = null;
    });
  }

  private _getInvoiceDeliveryMethods(isPayerChange?: boolean): void {
    if (this._getMethodsSub) {
      this._getMethodsSub.unsubscribe();
      this._getMethodsSub = null;
    }

    if (!this.company || !this.payer) {
      this.items = this.defaultItems;
      this._applyDefaultIfSelectedItemIsInvalid();
      return;
    }

    this._getMethodsSub = this._companyService.getInvoiceDeliveryMethods(this.company, this.payer).subscribe(invoiceDelivery => {
      if (isPayerChange && invoiceDelivery.isB2CActive) {
        this.control.setValue(InvoiceDeliveryMethod.EInvoiceB2C);
      }
      this.setDeliveryMethods(invoiceDelivery.methods);
      this._getMethodsSub = null;
    });
  }

  private _filterAllowedMethods(methods: InvoiceDeliveryMethod[]): InvoiceDeliveryMethod[] {
    return (methods ?? []).filter(method => this._isAllowedMethod(method));
  }

  private _isAllowedMethod(method: InvoiceDeliveryMethod): boolean {
    return !this.allowedMethods?.length || this.allowedMethods.includes(method);
  }
}
